import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';

import {
    AppBar,
    Avatar,
    Box,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import FeedbackIcon from '@mui/icons-material/Feedback';

import NavDrawer from './NavDrawer';
import SignOut from './SignOut';
import FeedbackDialog from './FeedbackDialog';

export default function NavBar() {
    const navigate = useNavigate();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [signOutOpen, setSignOutOpen] = useState(false);
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const getDisplayName = (user) => user?.display_name || (user ? `${user.first_name} ${user.last_name}` : '');

    const [accountName, setAccountName] = useState(() => getDisplayName(JSON.parse(localStorage.getItem('user'))));
    const [avatarUrl, setAvatarUrl] = useState(() => JSON.parse(localStorage.getItem('user'))?.avatar_url || '');
    const [devColorOverride, setDevColorOverride] = useState(() => localStorage.getItem('devColorOverride') !== 'false');
    const userRole = JSON.parse(localStorage.getItem('user'))?.role;
    const isEditorOrAdmin = userRole === 'editor' || userRole === 'admin';

    useEffect(() => {
        const updateAccountInfo = () => {
            const user = JSON.parse(localStorage.getItem('user'));
            setAccountName(getDisplayName(user));
            setAvatarUrl(user?.avatar_url || '');
            setDevColorOverride(localStorage.getItem('devColorOverride') !== 'false');
        };
        window.addEventListener('storage', updateAccountInfo);
        return () => window.removeEventListener('storage', updateAccountInfo);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const location = useLocation();
    if (location.pathname === '/login' || location.pathname === '/signup') return null;

    const recipePageMatch = location.pathname.match(/^\/recipe\/(\d+)/);
    const currentRecipeId = recipePageMatch ? recipePageMatch[1] : null;

    const handleSignOutConfirm = () => {
        Cookies.remove('token');
        localStorage.removeItem('user');
        sessionStorage.clear();
        window.dispatchEvent(new Event('storage'));
        setSignOutOpen(false);
        setAnchorEl(null);
        navigate('/login');
    };

    return (
        <AppBar position="sticky" sx={{ top: 0, zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: devColorOverride && /localhost|127\.0\.0\.1|-dev-/.test(process.env.REACT_APP_BACKEND_ADDRESS) ? '#5d4037' : undefined }}>
            <SignOut
                open={signOutOpen}
                onClose={() => setSignOutOpen(false)}
                onConfirm={handleSignOutConfirm}
            />
            <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} prefillRecipeId={currentRecipeId} />
            <NavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
            <Toolbar>
                {/* Left — menu button */}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                    <Tooltip title="Menu">
                        <IconButton
                            size="large"
                            edge="start"
                            color="inherit"
                            aria-label="open drawer"
                            onClick={() => setDrawerOpen(true)}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Centre — title */}
                <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
                        Sposito Family Cookbook
                    </Typography>
                </Link>

                {/* Right — account dropdown */}
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Box
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer' }}
                    >
                        <Avatar
                            src={avatarUrl || undefined}
                            sx={{ width: 28, height: 28, bgcolor: '#fff', color: '#B61219', fontSize: '0.75rem', fontWeight: 700 }}
                        >
                            {!avatarUrl && accountName.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </Avatar>
                        <Typography
                            variant="body2"
                            sx={{
                                display: { xs: 'none', sm: 'block' },
                                maxWidth: 120,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: 'rgba(255,255,255,0.9)',
                            }}
                        >
                            {accountName}
                        </Typography>
                    </Box>
                    <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={() => setAnchorEl(null)}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                        <MenuItem onClick={() => { setAnchorEl(null); navigate('/profile'); }}>
                            <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
                            View Profile
                        </MenuItem>
                        <MenuItem onClick={() => { setAnchorEl(null); navigate('/settings'); }}>
                            <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
                            Settings
                        </MenuItem>
                        {isEditorOrAdmin && (
                            <MenuItem onClick={() => { setAnchorEl(null); navigate('/admin'); }}>
                                <ListItemIcon><AdminPanelSettingsIcon fontSize="small" /></ListItemIcon>
                                Admin
                            </MenuItem>
                        )}
                        <MenuItem onClick={() => { setAnchorEl(null); setFeedbackOpen(true); }}>
                            <ListItemIcon><FeedbackIcon fontSize="small" /></ListItemIcon>
                            Send Feedback
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => { setAnchorEl(null); setSignOutOpen(true); }}>
                            <ListItemIcon><ExitToAppIcon fontSize="small" /></ListItemIcon>
                            Sign Out
                        </MenuItem>
                    </Menu>
                </Box>
            </Toolbar>
        </AppBar>
    );
}
